/**
 * Historian page shell (djin-cloud#806, task 18).
 *
 * Mounts the whole rebuilt feature: variable browser, chart, table,
 * selectors and panels built by PRs 1-6, on top of the `HistorianProvider`
 * (PR 5) and its deep-link seeding (task 17).
 *
 * Replaces the previous single-file Historian page (site/asset-type/device/
 * metric pickers over `useHistorianQuery`, a raw Recharts line chart, and an
 * ad-hoc HTML table) — the pieces that page owned now live in the components
 * imported below, and the old data layer (`hooks/useHistorian.ts`,
 * `utils/historianChartStyles.ts`) is retired (Step 7 / Ruling AJ: this page
 * was their only non-test consumer).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HistorianProvider, useHistorian } from '@/contexts/HistorianContext';
import { VariableBrowser } from '@/components/historian/VariableBrowser';
import { MultiAxisChart } from '@/components/historian/MultiAxisChart';
import { HistorianTable } from '@/components/historian/HistorianTable';
import { TimeRangeSelector } from '@/components/historian/TimeRangeSelector';
import { IntervalSelector } from '@/components/historian/IntervalSelector';
import { SelectedVariablesPanel } from '@/components/historian/SelectedVariablesPanel';
import { StatusBar } from '@/components/historian/StatusBar';
import { ChartErrorBoundary } from '@/components/historian/ChartErrorBoundary';
import { ExportOptionsModal } from '@/components/historian/ExportOptionsModal';
import { ToastContainer, useToasts } from '@/components/historian/Toast';
import { useHistorianShortcuts } from '@/hooks/useHistorianShortcuts';
import { parseHistorianUrlParams, parseHistorianSeriesUrl, writeHistorianUrl } from '@/utils/historianUrlParams';
import { Button, Card } from '@/components/gamma';

/** Shared frame for the Table pane's non-ready states, matching the chart's. */
function TablePlaceholder({ title, detail }: { title: string; detail: string }) {
  return (
    <Card variant="default" className="flex items-center justify-center h-full">
      <div className="text-center text-ems-text-secondary">
        <p className="text-lg mb-2">{title}</p>
        <p className="text-sm">{detail}</p>
      </div>
    </Card>
  );
}

/**
 * Table view with the same state coverage the chart has: no-selection,
 * loading, error, and ready. Reads the context directly rather than taking
 * props so the page shell's JSX stays a straight chart/table swap.
 */
function TablePane() {
  const { state } = useHistorian();

  if (state.selectedVariables.length === 0) {
    return (
      <TablePlaceholder
        title="No Variables Selected"
        detail="Select variables from the browser to see them in a table"
      />
    );
  }
  if (state.queryLoading) {
    return <TablePlaceholder title="Loading…" detail="Running the query" />;
  }
  if (state.queryError) {
    return (
      <TablePlaceholder
        title="Query failed"
        detail={state.queryError.message ?? 'The query could not be completed'}
      />
    );
  }
  if (!state.queryResult) {
    return (
      <TablePlaceholder
        title="No Results Yet"
        detail="Execute a query to populate the table"
      />
    );
  }

  return (
    <HistorianTable
      columns={['timestamp', ...state.selectedVariables.map((v) => v.alias)]}
      rows={state.queryResult.data}
      variables={state.selectedVariables}
      timeRange={state.timeRange}
    />
  );
}

function HistorianInner() {
  const { state, executeQuery, seedFromUrl } = useHistorian();
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [exportOpen, setExportOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const { toasts, addToast, dismissToast } = useToasts();
  const seeded = useRef(false);

  // One-shot deep-link seed (legacy #181 form first, then the series/q
  // form). ⚠️ `seeded` is a one-shot latch, not tidy-up debt — the sibling
  // effect below writes the URL on every new query result, and without this
  // ref those two effects form a write-URL -> re-seed -> execute -> write-URL
  // loop. PR 6 shipped a Critical of exactly this shape (an unbounded
  // `/v1/sites` retry storm, measured at 426 requests in 500 ms).
  //
  // ⚠️ Known, DELIBERATE residual (task-18-review.md M4): because the latch
  // is what stops that loop, it ALSO stops a second, genuinely different
  // deep link from re-seeding while this component stays mounted — e.g. an
  // in-app navigation from `/historian?series=A` to `/historian?series=B`
  // without a full page load. The deps array (`[location.search,
  // seedFromUrl]`) makes the effect notice the new `location.search`; the
  // latch is what then discards it. Left as-is on purpose: this page's only
  // production deep-link source is the daily-ops-report PDF link
  // (`backend/src/services/operations-report/historian-link.ts`), which is
  // an external `<a>` navigation — a full page load, a fresh mount, a fresh
  // `seeded.current = false`. Nothing in this codebase does an IN-APP
  // historian-to-historian navigation, and PR 8's E2E does not exercise one
  // either. Revisit if that changes; the fix would need to distinguish "a
  // human followed a new link" from "our own sibling effect just wrote
  // this," which is exactly the ambiguity the latch exists to collapse.
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const sp = new URLSearchParams(location.search);
    void seedFromUrl(parseHistorianUrlParams(sp) ?? parseHistorianSeriesUrl(sp));
  }, [location.search, seedFromUrl]);

  // Reflect the executed query in the URL so a chart can be shared.
  useEffect(() => {
    if (!state.queryResult) return;
    navigate(
      {
        search: writeHistorianUrl({
          series: state.selectedVariables,
          start: state.timeRange.start,
          end: state.timeRange.end,
          interval: state.interval,
          queryId: state.queryResult.queryId,
        }),
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on a new result
  }, [state.queryResult?.queryId]);

  useHistorianShortcuts({
    onExport: () => setExportOpen(true),
    onRefresh: () => void executeQuery(),
    onSearch: () => searchRef.current?.focus(),
    onEscape: () => setExportOpen(false),
  });

  return (
    <div className="grid grid-cols-[320px_1fr_360px] gap-4 h-[calc(100vh-7rem)] p-4">
      <VariableBrowser searchInputRef={searchRef} className="min-h-0" />

      <div className="flex flex-col min-h-0 gap-2">
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            variant={view === 'chart' ? 'primary' : 'ghost'}
            onClick={() => setView('chart')}
          >
            Chart
          </Button>
          <Button
            size="xs"
            variant={view === 'table' ? 'primary' : 'ghost'}
            onClick={() => setView('table')}
          >
            Table
          </Button>
          <div className="ml-auto flex gap-2">
            <Button
              size="xs"
              variant="secondary"
              onClick={() => void executeQuery()}
              disabled={state.queryLoading}
            >
              Refresh
            </Button>
            <Button
              size="xs"
              variant="secondary"
              onClick={() => setExportOpen(true)}
              disabled={!state.queryResult}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {state.queryResult?.metadata.dataThrough && (
          <div className="text-xs text-ems-amber border border-ems-amber/40 rounded px-3 py-2">
            {/*
              djin-cloud#832. Charts are served from the pre-aggregated rollups, which trail
              live telemetry by minutes. The server normally closes that gap from the raw
              table, but when a rollup has fallen too far behind, filling it would cost more
              than the query budget allows — so the series genuinely stops early.
              Saying so is the whole point: a chart that just ends reads as "the device
              stopped reporting", which is the one conclusion an operator must not draw here.
            */}
            Live data unavailable — the pre-aggregated data is
            {typeof state.queryResult.metadata.rollupLagSeconds === 'number'
              ? ` ${Math.round(state.queryResult.metadata.rollupLagSeconds / 60)} minutes`
              : ''}{' '}
            behind. Showing through{' '}
            <strong>{new Date(state.queryResult.metadata.dataThrough).toLocaleString()}</strong>.
            This is a data-pipeline delay, not a gap in the equipment's reporting.
          </div>
        )}

        {state.queryResult?.metadata.truncated && (
          <div className="text-xs text-ems-amber border border-ems-amber/40 rounded px-3 py-2">
            {/*
              djin-cloud#812. The old copy said only "truncated", which left the operator to
              guess WHICH end was missing — and the server used to drop the NEWEST rows, so
              the honest reading was the opposite of the intuitive one. It now keeps live data
              and cuts history, and the banner has to say so: a chart that silently starts
              late reads as "the device came online then", not as "we ran out of budget".
            */}
            Result truncated at the row cap — the <strong>oldest</strong> data was dropped;
            the most recent is shown.
            {state.queryResult.metadata.completeFrom && (
              <> All selected series are complete from{' '}
                <strong>{new Date(state.queryResult.metadata.completeFrom).toLocaleString()}</strong>.</>
            )}
            {' '}Widen the interval or narrow the range to cover more history.
          </div>
        )}

        <div className="flex-1 min-h-0">
          {/* Both branches are wrapped: `HistorianTable` is new code on a new
              dependency (react-window) formatting live query values per cell, so
              an unexpected value shape there would otherwise crash the whole
              page rather than being contained to this pane the way a chart
              error already is. The boundary's name predates the table; it is
              not chart-specific. */}
          <ChartErrorBoundary onReset={() => void executeQuery()}>
            {view === 'chart' ? (
              <MultiAxisChart height={560} />
            ) : (
              /* Table view must distinguish the same four states the chart
                 does. `state.queryResult && <HistorianTable/>` alone rendered
                 NOTHING — not even a card frame — for a user who switched to
                 Table before querying, during the first load, or after an
                 error, which is reachable by deep-linking into a series that
                 fails. */
              <TablePane />
            )}
          </ChartErrorBoundary>
        </div>

        <StatusBar />
      </div>

      <div className="flex flex-col gap-4 min-h-0 overflow-y-auto">
        <TimeRangeSelector />
        <IntervalSelector />
        <SelectedVariablesPanel onViewChart={() => setView('chart')} />
      </div>

      <ExportOptionsModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        onExportComplete={(ok, msg) => addToast({ type: ok ? 'success' : 'error', message: msg })}
      />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

export default function Historian() {
  return (
    <HistorianProvider>
      <HistorianInner />
    </HistorianProvider>
  );
}
